import React, {
    FC,
    RefObject,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import { CampaignCardProps } from "../ActionItem";
import { ActionCardCompleted } from "../ActionCardCompleted";
import { ActionCloseButton } from "../ActionCloseButton";
import { ActionCardSection } from "../ActionCardSection";
import { ReceiptIcon } from "../../../icons";
import { ActionStatus } from "../../../common/types/actions";
import { Button } from "../../general/Button/Button";
import { ActionConfirmButton } from "../ActionConfirmButton";
import { ActionCard } from "../ActionCard";
import { FinancialDocumentUploadContextValue } from "../../documents/FinancialDocumentUpload/financialDocumentUploadContext";
import { FinancialDocumentUploadProvider } from "../../documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FinancialDocument } from "../../../common/types/financialDocument";
import { useEntities } from "../../../hooks/useEntities";
import { TransactionSortValue, useSort } from "../../transactions/useSort";
import { useTransactionsQuery } from "../../transactions/useTransactionsQuery";
import { ReceiptsUploadMetaDto } from "../../../common/dto/actions/receipts-upload-meta.dto";
import { UploadReceiptsTransactionsSection } from "./transactions/UploadReceiptsTransactionsSection";
import { UploaderSectionBody } from "./uploader/UploaderSectionBody";
import {
    UploadReceiptsActionContext,
    uploadReceiptsActionContext,
} from "./uploadReceiptsAction.context";
import { UploadReceiptsDropzone } from "./uploader/UploadReceiptsDropzone";
import styles from "./UploadReceipts.module.scss";

export const UploadReceiptsCard: FC<
    CampaignCardProps<ReceiptsUploadMetaDto>
> = ({ action, onConfirm, onSkip, isLoading, ...rest }) => {
    const uploaderRef = useRef<FinancialDocumentUploadContextValue>();
    const { isCompleted, opened } = rest;

    const entities = useEntities();
    const { sortExpression } = useSort(TransactionSortValue.AMOUNT_DESC);
    const [uploadedDocumentIds, setUploadedDocumentIds] = useState<number[]>(
        [],
    );

    const {
        query: {
            data: transactionsResponse,
            isLoading: isLoadingTransactions,
            refetch,
        },
    } = useTransactionsQuery(
        {
            sort: sortExpression,
            filters: {
                entityIds: entities
                    .filter((entity) => entity.isBusiness)
                    .map((entity) => entity.id),
                requiresReceipt: true,
            },
            page: 1,
            limit: 0,
        },
        { enabled: opened, refetchOnWindowFocus: false },
    );

    const confirmAction = useCallback(() => {
        onConfirm?.({
            ...action,
            meta: {
                uploadedReceipts: uploadedDocumentIds,
                skippedTransactions: [],
            },
        });
    }, [action, onConfirm, uploadedDocumentIds]);

    const skipAction = useCallback(() => {
        onSkip?.({
            ...action,
            meta: {
                ...action.meta,
                skippedTransactions: [
                    ...(transactionsResponse?.data ?? []).map(
                        (transaction) => transaction.id,
                    ),
                ],
                uploadedReceipts: uploadedDocumentIds,
            },
        });
    }, [action, onSkip, transactionsResponse?.data, uploadedDocumentIds]);

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            setUploadedDocumentIds((prev) => [
                ...prev,
                ...uploadedDocuments.map((doc) => doc.id),
            ]);
        },
        [],
    );

    const contextValue: UploadReceiptsActionContext = useMemo(
        () => ({
            refreshTransactions: refetch,
        }),
        [refetch],
    );

    const firstSection = (inOpened: boolean, closeCard: () => void) =>
        isCompleted ? (
            <ActionCardCompleted header={`Receipts Uploaded`}>
                <ActionCloseButton onClick={closeCard} />
            </ActionCardCompleted>
        ) : (
            <ActionCardSection
                entity={action.entity}
                inOpened={inOpened}
                header="Catch up on your receipts"
                icon={<ReceiptIcon />}
                ctas={
                    <>
                        {action.status !== ActionStatus.SKIPPED && (
                            <Button
                                variant="secondary"
                                onClick={() => skipAction()}
                            >
                                Skip
                            </Button>
                        )}
                        <ActionConfirmButton
                            loading={isLoading}
                            onClick={confirmAction}
                            disabled={
                                isLoadingTransactions ||
                                transactionsResponse?.total !== 0
                            }
                        />
                    </>
                }
                isTitle
            />
        );

    const otherSections = (inOpened: boolean) => (
        <uploadReceiptsActionContext.Provider value={contextValue}>
            <UploadReceiptsTransactionsSection
                inOpened={inOpened}
                isLoading={isLoadingTransactions}
                data={transactionsResponse}
                onDetailsViewClosed={refetch}
            />

            <ActionCardSection
                header="Drop in the receipts below and Kick will automatically match them"
                inOpened={inOpened}
            >
                <div className={styles.body}>
                    <UploadReceiptsDropzone>
                        <UploaderSectionBody
                            onFinancialDocumentStatusUpdated={refetch}
                        />
                    </UploadReceiptsDropzone>
                </div>
            </ActionCardSection>
        </uploadReceiptsActionContext.Provider>
    );

    return (
        <FinancialDocumentUploadProvider
            onCompleted={handleUploadCompleted}
            ref={uploaderRef as RefObject<FinancialDocumentUploadContextValue>}
        >
            <ActionCard
                {...rest}
                firstSection={firstSection}
                otherSections={otherSections}
            />
        </FinancialDocumentUploadProvider>
    );
};
